import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { ContentBox } from '@/common/layout/ContentBox';
import { ScrollableSpacerLayoutArea } from '@/common/layout/ScrollableSpacerLayoutArea';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { faSave, faTimes } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { cloneElement, isValidElement, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form } from '../../form/Form';
import { EntityAuditCard } from '../../form/cards/EntityAuditCard';
import { EntitySectionCard } from '../../form/cards/EntitySectionCard';
import { EntityStatusCard } from '../../form/cards/EntityStatusCard';
import { useEntityFromRoute } from '../../hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '../../hooks/route/useModuleFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { usePrincipal } from '../../principal/PrincipalContext';
import { useRsrc } from '../../rsrc/RsrcContext';
import { EntityUtils } from '../../utils/EntityUtils';
import { ExprUtils } from '../../utils/ExprUtils';
import { useTab } from '../entityTabs/TabContext';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { EntityFormMode } from '../enumerations/EntityFormMode';
import { FieldDefsProvider } from './FieldDefsContext';

export const EntityForm = ({
  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Optional Entity ID. If not provided it will be retrieved from navState
  entityId: entityIdProp,

  // Optional Modifications to EntityForm
  augFieldDefs,

  // Optional: transforms the form's values into one or more objects to save (default: a single save of the form's values)
  augSaveEntityObjs,
  children,

  // Embedded usage (e.g. inside a dialog). Providing a mode detaches the form from the page-level
  // entityFormMode and from navState/history navigation; results are reported via onSaved/onCancel
  mode: modeProp,
  initialValues,
  onSaved,
  onCancel,
  hideButtons = false,
  compact = false,
  // Extra content appended to the left (field sections) column, e.g. a related-entity card list
  extraLeftContent,
  ref,
}) => {
  const { tabLabel } = useTab();

  // The "Free" EntityForm must either be housed in an Info tab, or as a standalone component without any tabs
  if (!(tabLabel === 'Info' || tabLabel === null) && !entity) {
    throw new Error(`An Entity needs to be provided to the EntityForm in tab ${tabLabel}. This is a development error`);
  }

  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  const { navState, assign } = useNavState();
  const rsrc = useRsrc();
  const entityMgr = useEntityMgr();
  const { getFieldRoleSet } = usePrincipal();

  const embedded = modeProp != null;
  const [embeddedMode, setEmbeddedMode] = useState(modeProp);
  const entityFormMode = embedded ? embeddedMode : entityMgr.entityFormMode;
  const setEntityFormMode = embedded ? setEmbeddedMode : entityMgr.setEntityFormMode;
  const asOfDate = embedded ? null : entityMgr.asOfDate;

  const [fieldDefs, setFieldDefs] = useState();
  const [entityId, setEntityId] = useState(entityIdProp ?? (embedded ? null : navState.entityId));

  const formRef = useRef();
  const loadedValuesRef = useRef({});
  const entityFormModeRef = useRef(entityFormMode);
  entityFormModeRef.current = entityFormMode;

  useImperativeHandle(ref, () => ({
    save: () => save(),
    cancel: () => cancel(),
    isDirty: () => !sameValues(formRef.current?.formValues, loadedValuesRef.current),
    getFormValues: () => formRef.current?.formValues,
  }));

  useEffect(() => {
    rsrc.fieldDefs(module, entity).then((fieldDefs) => {
      // Field definitions may be cached by RsrcContext; form-specific mutations must stay local.
      fieldDefs = fieldDefs.map((fieldDef) => ({ ...fieldDef }));

      // TODO: Map field type to form field component

      if (augFieldDefs) {
        const fieldToFieldDef = Object.fromEntries(fieldDefs.map((fieldDef) => [fieldDef.field, fieldDef]));
        augFieldDefs(fieldToFieldDef);
        fieldDefs = Object.values(fieldToFieldDef);
      }

      fieldDefs = fieldDefs.filter((fieldDef) => !fieldDef.checkboxSelection);
      fieldDefs = EntityUtils.filterAuthorizedFields(module, entity, fieldDefs, getFieldRoleSet);

      fieldDefs.sort((a, b) => (a.precedence || 0) - (b.precedence || 0));

      setDisabled(fieldDefs);
      setFieldDefs(fieldDefs);
    });
  }, []);

  const { refreshTrigger } = useEntityChangeSubscr(module, entity, ExprUtils.whereParams('id', entityId));

  // Responsible for setting the disabled status of fieldDefs
  const setDisabled = (fieldDefs, formValues = formRef.current?.formValues) => {
    if (!fieldDefs) return;
    const mode = entityFormModeRef.current;
    fieldDefs.forEach((fieldDef) => {
      if (mode === EntityFormMode.VIEW) {
        fieldDef.disabled = true;
      } else if (mode === EntityFormMode.ADD) {
        fieldDef.disabled = fieldDef.editLevel === 'NEVER';
      } else {
        fieldDef.disabled =
          fieldDef.editLevel === 'NEVER' ||
          fieldDef.editLevel === 'ADD_ONLY' ||
          (fieldDef.editLevel === 'SOR' && !formValues?.sor);
      }

      // augFieldDefs components capture the fieldDef object they were created with; rebind them after cloning.
      if (isValidElement(fieldDef.component) && fieldDef.component.props.fieldDef) {
        fieldDef.component = cloneElement(fieldDef.component, { fieldDef });
      }
    });
  };

  // Subscribe to the mode
  useEffect(() => {
    if (!fieldDefs) return;
    setDisabled(fieldDefs);
    setFieldDefs([...fieldDefs]); // Putting the fieldDefs in a new array reference forces an update
  }, [entityFormMode]);

  const refreshForm = () => {
    const currFormRef = formRef.current;
    if (currFormRef == null) return;

    // If adding, we use anything supplied in the navState entity onlys
    if (entityFormMode === EntityFormMode.ADD) {
      const addValues = embedded ? (initialValues ?? {}) : navState.entityObj;
      loadedValuesRef.current = addValues ?? {};
      currFormRef.setFormValues(addValues);
      return;
    }

    const rowsReq = {};
    const exprs = [ExprUtils.whereParams('id', entityId)];

    // As Of Date
    if (asOfDate) {
      exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', asOfDate));
      exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER_OR_EQUAL', asOfDate));
      rowsReq.hist = true;
    }

    rowsReq.expr = ExprUtils.and(...exprs);

    if (navState.partition) {
      // rowsReq.partition = navState.partition;
    }

    rsrc.one(module, entity, rowsReq).then((entityObj) => {
      entityObj ??= {}; // TODO: What to do if asOfDate is before Entity's creation?
      loadedValuesRef.current = entityObj;
      currFormRef.setFormValues(entityObj);
      // EDIT dialogs open before their entity is loaded; SOR fields must be recalculated with the loaded row.
      setFieldDefs((currentFieldDefs) => {
        const nextFieldDefs = currentFieldDefs.map((fieldDef) => ({ ...fieldDef }));
        setDisabled(nextFieldDefs, entityObj);
        return nextFieldDefs;
      });
    });
  };

  // Subscribe to the refresh button, asOfDate, and entityId change (this happens after saving a new Entity)
  useEffect(() => {
    if (fieldDefs == null) return;
    refreshForm();
  }, [refreshTrigger, asOfDate, entityId, fieldDefs != null]);

  const save = () => {
    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const newEntityObj = formRef.current.formValues;
    const entityObjsToSave = augSaveEntityObjs ? augSaveEntityObjs(newEntityObj) : [newEntityObj];

    // One request for the whole submission (the save endpoint accepts an array)
    rsrc.save(module, entity, entityObjsToSave).then((savedEntityObjs) => {
      if (embedded) {
        onSaved?.(savedEntityObjs.length > 1 ? savedEntityObjs : savedEntityObjs[0]);
        return;
      }

      // Multiple Entities were created from a single form submission; go back to wherever this form was opened from
      if (savedEntityObjs.length > 1) {
        window.history.back();
        return;
      }

      const [entityObj] = savedEntityObjs;
      assign('entityId', entityObj.id);
      setEntityId(entityObj.id);
      setEntityFormMode(EntityFormMode.VIEW);
    });
  };

  const cancel = () => {
    if (embedded) {
      onCancel?.();
      return;
    }

    if (entityFormMode === EntityFormMode.ADD) {
      window.history.back();
      return;
    }

    setEntityFormMode(EntityFormMode.VIEW);
    refreshForm();
  };

  if (!fieldDefs) return;

  return (
    <FieldDefsProvider fieldDefs={fieldDefs}>
      <ContentBox className={entityFormMode === EntityFormMode.VIEW ? 'entity-form-wrapper' : null}>
        {/* The className on this box changes the styling of disabled form fields so that in VIEW mode they are more visible */}

        <Form ref={formRef}>
          {children ??
            (compact ? renderCompactChildren(fieldDefs) : renderDefaultChildren(fieldDefs, extraLeftContent))}
        </Form>

        {entityFormMode !== EntityFormMode.VIEW && !hideButtons && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              columnGap: '10px',
            }}
          >
            <Button color="secondary" variant="outlined" sx={{ minWidth: '100px' }} onClick={cancel}>
              <Box component="span" sx={{ mr: 1.3 }}>
                <FontAwesomeIcon icon={faTimes} />
              </Box>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              sx={{ minWidth: '100px' }}
              data-test-id="save-button"
              onClick={save}
            >
              <Box component="span" sx={{ mr: 1.3 }}>
                <FontAwesomeIcon icon={faSave} />
              </Box>
              Save
            </Button>
          </Box>
        )}
      </ContentBox>
    </FieldDefsProvider>
  );
};

// Ignores null/blank values so fields registering themselves on mount don't count as edits
const sameValues = (a, b) => {
  const normalize = (obj) =>
    Object.entries(obj ?? {})
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
};

// Single-column layout for dialogs: sections stacked, no Status/Audit cards
const renderCompactChildren = (fieldDefs) => {
  const sections = [...new Set(fieldDefs.map((fieldDef) => fieldDef.section))].filter(
    (section) => section !== 'Status' && section !== 'Audit',
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sections.map((section) => (
        <EntitySectionCard key={section} title={section} />
      ))}
    </Box>
  );
};

const renderDefaultChildren = (fieldDefs, extraLeftContent) => {
  // Group fieldDefs by section
  const sectionToFieldDefs = {};
  fieldDefs.forEach((fieldDef) => {
    const section = fieldDef.section;
    if (!sectionToFieldDefs[section]) sectionToFieldDefs[section] = [];
    sectionToFieldDefs[section].push(fieldDef);
  });

  // Status and Audit are special and rendered on the right; everything else is stacked on the left
  const leftSections = Object.keys(sectionToFieldDefs).filter((section) => section !== 'Status' && section !== 'Audit');

  return (
    <SpacerLayout minHeight={600}>
      <Spacer weights={[3, 1]}>
        <ScrollableSpacerLayoutArea>
          {leftSections.map((section) => (
            <EntitySectionCard key={section} title={section} />
          ))}
          {extraLeftContent}
        </ScrollableSpacerLayoutArea>
        <ScrollableSpacerLayoutArea>
          <EntityStatusCard />
          <EntityAuditCard />
        </ScrollableSpacerLayoutArea>
      </Spacer>
    </SpacerLayout>
  );
};
